import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack, MenuItem, Typography, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useForm } from 'react-hook-form';
import { toastError } from 'src/utils/format-error';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { LoadingButton } from '@mui/lab';
import { UserType } from 'src/type/user';
import axios, { endpoints } from 'src/utils/axios';
import { enqueueSnackbar } from 'notistack';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { levelOptions } from './utils';
import EditEnableDialog from './components/edit-enable-dialog';

const Schema = Yup.object().shape({
  firstName: Yup.string(),
  lastName: Yup.string(),
  email: Yup.string(),
  phoneNumber: Yup.string()
    .nullable() // อนุญาตให้เป็นค่าว่างหรือ null ได้
    .notRequired() // ไม่จำเป็นต้องกรอก
    // .matches(/^0\d{9}$/, 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลักและขึ้นต้นด้วย 0')
    .test(
      'is-valid-phone',
      'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลักและขึ้นต้นด้วย 0',
      (value) => !value || /^0\d{9}$/.test(value) // เช็คเฉพาะตอนที่มีค่า
    ),
  level: Yup.string().required('จำเป็นต้องระบุระดับผู้ใช้งาน'),
  isPreRegistered: Yup.boolean().notRequired(),
});

type FormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string | null;
  level: string;
  isPreRegistered?: boolean | null;
};

type Props = {
  user: UserType;
  handleReload?: VoidFunction;
  onUserUpdate?: (updatedUser: UserType) => void;
};

export default function UserEditForm({ user, handleReload, onUserUpdate }: Props) {
  const editEnableDialog = useBoolean();
  const confirmUpdateDialog = useBoolean();

  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      level: user?.level || '',
      isPreRegistered: user?.isPreRegistered ?? false,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, dirtyFields },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await axios.patch(endpoints.users.update(user.uid), data);
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const handleClickUpdate = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      confirmUpdateDialog.onTrue();
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      await onSubmit();
    } finally {
      confirmUpdateDialog.onFalse();
    }
  };

  const formLevelOptions = levelOptions.filter((option) => option.value !== 'all');

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="subtitle1">สถานะบัญชี</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Label
              variant="soft"
              color={(user.isEnable === true && 'success') || 'error'}
              sx={{ width: 'fit-content', px: 1 }}
            >
              {user.isEnable === true ? 'ปกติ' : 'โดนแบน'}
            </Label>

            <IconButton onClick={editEnableDialog.onTrue}>
              <Iconify icon="mdi:pencil" />
            </IconButton>
          </Stack>
        </Stack>

        <Typography variant="subtitle1">อัพเดทข้อมูล</Typography>
        <Grid container>
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="firstName"
                  label="ชื่อ"
                  placeholder="ชื่อ"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="lastName"
                  label="นามสกุล"
                  placeholder="นามสกุล"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="email"
                  label="อีเมล"
                  placeholder="example@gmail.com"
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    dirtyFields?.email ? 'กรุณาตรวจสอบอีเมลให้ถูกต้องก่อนบันทึก' : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="phoneNumber"
                  label="เบอร์ติดต่อ"
                  placeholder="0XXXXXXXXX"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <RHFSelect
                  name="level"
                  label="ระดับ"
                  InputLabelProps={{ shrink: true }}
                  placeholder="เลือกระดับ"
                >
                  {formLevelOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>
              <Grid item xs={12}>
                <RHFSwitch
                  name="isPreRegistered"
                  label="ลงทะเบียนล่วงหน้า"
                  sx={{ alignItems: 'center', justifyContent: 'center' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box>
          <LoadingButton
            variant="contained"
            color="warning"
            loading={isSubmitting}
            onClick={handleClickUpdate}
          >
            อัพเดทข้อมูล
          </LoadingButton>
        </Box>
      </Stack>

      <Dialog
        open={confirmUpdateDialog.value}
        onClose={confirmUpdateDialog.onFalse}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>ยืนยันการอัพเดทข้อมูล</DialogTitle>
        <DialogContent>ต้องการอัพเดทข้อมูลผู้ใช้งานใช่หรือไม่?</DialogContent>
        <DialogActions>
          <Button onClick={confirmUpdateDialog.onFalse} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton onClick={handleConfirmUpdate} loading={isSubmitting} variant="contained">
            ยืนยัน
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <EditEnableDialog
        user={user}
        open={editEnableDialog.value}
        onClose={editEnableDialog.onFalse}
        handleReload={handleReload || (() => {})}
        onUserUpdate={onUserUpdate}
      />
    </FormProvider>
  );
}
